"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";
import Image from "next/image";
import { LoginForm } from "@/components/organisms/login-form";

export function AuthenticationPageClient() {
  const router = useRouter();
  const { user, isLoading, isLoggingOut, logout } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectAttempts, setRedirectAttempts] = useState(0);
  const [redirectTimeout, setRedirectTimeout] = useState<NodeJS.Timeout | null>(null);
  const [forceReset, setForceReset] = useState(false);

  // Safety mechanism: reset auth state jika stuck terlalu lama
  useEffect(() => {
    if (forceReset) {
      console.log("Force reset triggered, clearing all auth state");
      localStorage.removeItem("token");
      sessionStorage.removeItem("user");
      setIsRedirecting(false);
      setRedirectAttempts(0);
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
        setRedirectTimeout(null);
      }
      logout();
      setForceReset(false);
    }
  }, [forceReset, logout, redirectTimeout]);

  // Function untuk verify token
  const verifyToken = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        if (user) {
          console.log("No token found but user exists, logging out");
          logout(); // Force logout jika ada user tapi tidak ada token
        }
        return false;
      }

      const response = await fetch("/api/verify-token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Token expired atau invalid
        console.log("Token verification failed, logging out");
        localStorage.removeItem("token");
        sessionStorage.removeItem("user");
        logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error verifying token:", error);
      // Jika ada error, clear semua dan logout
      localStorage.removeItem("token");
      sessionStorage.removeItem("user");
      logout();
      return false;
    }
  };

  useEffect(() => {
    const handleAuthRedirect = async () => {
      // Clear any existing redirect timeout
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
        setRedirectTimeout(null);
      }

      // Jika user ada dan tidak dalam loading/logout state
      if (!isLoading && !isLoggingOut && user && !isRedirecting) {
        setIsRedirecting(true);
        
        // Set timeout untuk redirect stuck (maksimal 10 detik)
        const timeout = setTimeout(() => {
          console.log("Redirect timeout reached, triggering force reset");
          setForceReset(true);
        }, 10000);
        setRedirectTimeout(timeout);
        
        // Verify token sebelum redirect
        const isTokenValid = await verifyToken();
        
        if (isTokenValid && user) { // Double check user still exists after verify
          // Token valid, bisa redirect
          console.log("Token valid, redirecting to dashboard");
          clearTimeout(timeout);
          setRedirectTimeout(null);
          router.push("/dashboard");
        } else {
          // Token invalid atau user sudah tidak ada, reset redirecting state
          console.log("Token invalid or user cleared, staying on login page");
          clearTimeout(timeout);
          setRedirectTimeout(null);
          setIsRedirecting(false);
          setRedirectAttempts(0);
        }
      }
    };

    handleAuthRedirect();
  }, [user, isLoading, isLoggingOut, router, isRedirecting, logout, redirectTimeout]);

  // Enhanced check untuk auth state mismatch dan timeout
  useEffect(() => {
    const checkAuthState = async () => {
      const userInStorage = sessionStorage.getItem("user");
      const tokenInStorage = localStorage.getItem("token");
      
      // Case 1: Ada user state tapi tidak ada storage
      if (user && (!userInStorage || !tokenInStorage) && !isLoading && !isLoggingOut) {
        console.log("Auth state mismatch - no storage data, logging out");
        setIsRedirecting(false);
        setRedirectAttempts(0);
        if (redirectTimeout) {
          clearTimeout(redirectTimeout);
          setRedirectTimeout(null);
        }
        logout();
        return;
      }

      // Case 2: User stuck di redirecting terlalu lama
      if (isRedirecting && user) {
        const currentAttempts = redirectAttempts + 1;
        setRedirectAttempts(currentAttempts);
        
        if (currentAttempts >= 5) { // Increased threshold
          console.log("Too many redirect attempts, triggering force reset");
          setForceReset(true);
        }
      }

      // Case 3: Check for orphaned redirecting state
      if (isRedirecting && !user) {
        console.log("Redirecting state without user, resetting");
        setIsRedirecting(false);
        setRedirectAttempts(0);
        if (redirectTimeout) {
          clearTimeout(redirectTimeout);
          setRedirectTimeout(null);
        }
      }
    };

    const timer = setTimeout(checkAuthState, 2000);
    return () => clearTimeout(timer);
  }, [user, isLoading, isLoggingOut, isRedirecting, redirectAttempts, logout, redirectTimeout]);

  // Reset redirecting state dan attempts jika user berubah menjadi null
  useEffect(() => {
    if (!user && isRedirecting) {
      console.log("User cleared, resetting redirect state");
      setIsRedirecting(false);
      setRedirectAttempts(0);
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
        setRedirectTimeout(null);
      }
    }
  }, [user, isRedirecting, redirectTimeout]);

  // Cleanup timeout saat component unmount
  useEffect(() => {
    return () => {
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
      }
    };
  }, [redirectTimeout]);

  // Jika sedang logout, tampilkan logout message dan jangan redirect
  if (isLoggingOut) {
    return (
      <div className="container relative min-h-screen flex-col items-center justify-center grid">
        <div className="flex items-center justify-center">
          <div className="text-lg">Logging out...</div>
        </div>
      </div>
    );
  }

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="container relative min-h-screen flex-col items-center justify-center grid">
        <div className="flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  // Jika user sudah login, tampilkan loading karena akan redirect
  if (user) {
    return (
      <div className="container relative min-h-screen flex-col items-center justify-center grid">
        <div className="flex items-center justify-center">
          <div className="text-lg">Redirecting to dashboard...</div>
        </div>
      </div>
    );
  }

  // Jika tidak ada user (belum login), tampilkan form login
  return (
    <div className="container relative min-h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-quinaSecondary" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          Quina Breadhouse
        </div>
        <div className="relative z-20 mt-auto">
          <blockquote className="space-y-2 text-secondary">
            <p className="text-lg">
              &ldquo;This POS system has streamlined our operations and improved our customer service significantly.&rdquo;
            </p>
            <footer className="text-sm">Sofia Davis</footer>
          </blockquote>
        </div>
      </div>
      <div className="lg:p-8 w-full justify-center items-center">
        <div className="text-center mb-5">
          <Image src={"/quina.png"} alt={""} width={100} height={100} className="mx-auto" priority style={{width: "auto", height: "auto"}}/>
        </div>
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Login to your account
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your credentials below to access the POS system
            </p>
          </div>
          <LoginForm />
        </div>
      </div>
    </div>
  );
}